import * as posActions from "../../lib/posActions";
import * as routeHelpers from "../../lib/routeHelpers";
import AllergyWarningModal from "../ComboCustomization/AllergyWarningModal";
import cn from "classnames";
import ComboSubstitutionsModal from "../ComboSubstitutionsModal";
import CustomizationList from "../CustomizationList";
import Drawer from "../Drawer";
import getInitialModSelections from "../../lib/getInitialModSelections";
import ItemEditor from "../ItemEditor";
import logger from "../../lib/sentryLogger";
import OutlineButton from "../OutlineButton";
import QuantityControl from "../QuantityControl";
import React, {useState, useEffect, useMemo} from "react";
import RetinaImage from "../RetinaImage";
import SplitDrawerContent from "../SplitDrawerContent";
import styles from "./GroupingCustomization.module.css";
import ToggleButton from "../ToggleButton";
import {API, Lib} from "habit-core";
import {useNavigate, useParams} from "react-router-dom";
import {useAppDispatch, useAppSelector} from "../../lib/hooks";
import {v4 as uuid4} from "uuid";

const strings = {
    addToOrder: "Add To Order",
    updateOrder: "Update Order",
    cancel: "Cancel",
    addOnsTitle: "Suggested Add-Ons",
    makeItACombo: "Make it a Combo",
};

type Params = {
    categoryId: string;
    groupingId: string;
};

function getUnitPriceCents(
    menuItemPriceCents: API.models.USDCents,
    modifierSelections: API.models.ModifierSelectionDict,
    menuItemModifiers: {[modifierId: string]: API.models.MenuItemModifier},
    allPreps: {byId: {[modifierId: string]: Lib.selectors.ItemModifierData}},
): API.models.USDCents {
    let perUnitPriceCents = menuItemPriceCents;
    Object.keys(modifierSelections).forEach((modId) => {
        const mod = menuItemModifiers[modId];
        if (mod) {
            const [sel] = mod.selections.filter(
                (x) => x.id === modifierSelections[modId]?.selectionId,
            );

            if (sel && sel.priceCents) {
                perUnitPriceCents +=
                    sel.priceCents * modifierSelections[modId]?.quantity;
            }
        } else {
            const allPrepsMod = allPreps.byId[modId];
            const allPrepModSelection = allPrepsMod?.selections.find(
                (selection) =>
                    selection.id === modifierSelections[modId]?.selectionId,
            );

            if (allPrepModSelection && allPrepModSelection.priceCents) {
                perUnitPriceCents +=
                    allPrepModSelection.priceCents *
                    modifierSelections[modId]?.quantity;
            }
        }
    });

    return perUnitPriceCents;
}

export default function GroupingCustomization() {
    const navigate = useNavigate();
    const {categoryId, groupingId} = useParams<Params>();
    const menuId = useAppSelector((state) => state.currentOrder.menuId);
    useEffect(() => {
        if (!menuId || !categoryId || !groupingId) {
            logger.warn(
                "Attempting to customize grouping. Invalid menu id, category id, and/or grouping id",
                {
                    menuId: menuId,
                    categoryId: categoryId,
                    groupingId: groupingId,
                },
            );
            navigate(routeHelpers.menu(), {replace: true});
        }
    }, []);

    const grouping = useAppSelector((state) =>
        groupingId ? state.groupings.byId[groupingId] : null,
    );
    const itemsById = useAppSelector((state) => state.items.byId);
    const menuItems = useAppSelector((state) => state.menuItems.byMenuId);
    const menuItemModifiers = useAppSelector(
        (state) => state.menuItemModifiers.byMenuId,
    );
    const menuCombos = useAppSelector((state) => state.menuCombos.byMenuId);

    const availableOptions =
        grouping?.options.filter((option) => {
            if (option.itemId) {
                return !!menuItems[menuId ?? ""][option.itemId];
            } else if (option.comboId) {
                return !!menuCombos[menuId ?? ""][option.comboId];
            }
            return false;
        }) ?? [];

    useEffect(() => {
        if (!grouping) {
            logger.warn(
                "Attempting to customize grouping. Grouping not found",
                {
                    groupingId: groupingId,
                },
            );
            navigate(routeHelpers.menu(), {replace: true});
        } else if (availableOptions.length === 0) {
            logger.warn(
                "Attempting to customize grouping. No available options",
                {
                    groupingId: groupingId,
                },
            );
            navigate(routeHelpers.menu(), {replace: true});
        }
    }, []);

    const showDigitalOnly = useAppSelector(
        (state) =>
            !!state.pos.currentOrder.orderNumber &&
            (state.pos.station.mode === "front_of_house_1" ||
                state.pos.station.mode === "front_of_house_2"),
    ); // order is pending

    const dispatch = useAppDispatch();
    const [quantity, setQuantity] = useState(1);

    const posAllPrepsMods = useAppSelector((state) => state.pos.allPreps);
    const allergyWarningShown = useAppSelector(
        (state) => state.pos.currentOrder.allergyWarningShown,
    );
    const [showAllergyWarning, setShowAllergyWarning] = useState(false);

    const onClose = () =>
        navigate(
            categoryId
                ? routeHelpers.category(categoryId)
                : routeHelpers.menu(),
        );

    const [activeOptionId, setActiveOptionId] = useState(
        availableOptions[0].itemId ?? "",
    );
    const [modSelections, setModSelections] =
        useState<API.models.ModifierSelectionDict>(
            getInitialModSelections(
                itemsById,
                activeOptionId,
                menuItems,
                menuId ?? "",
                menuItemModifiers,
                null,
                true,
                !showDigitalOnly,
            ),
        );

    const getItemComboSubstitutions = useMemo(
        Lib.selectors.createGetItemComboSubstitutions,
        [],
    );
    const matchedCombos = useAppSelector((state) =>
        activeOptionId
            ? getItemComboSubstitutions(state, activeOptionId)
            : null,
    );
    const [comboSubstitutionsModalVisible, setComboSubstitutionsModalVisible] =
        useState(false);
    const onClickCombo = (categoryId: string, comboId: string) => {
        navigate(routeHelpers.combo(categoryId, comboId), {
            state: {
                comboUpgrade: {
                    entreeComboItem: {
                        itemId: activeOptionId,
                        modifierSelections: modSelections,
                    },
                },
            },
        });
    };

    const addOns = useAppSelector(
        (state) => state.pos.addOns.byId[activeOptionId],
    );
    const unfilteredAddOnsInfo = addOns?.map((addOnId) => {
        const addOnMenuItem = menuItems[menuId ?? ""][addOnId];
        const addOnItem = itemsById[addOnId];
        if (addOnMenuItem) {
            return {
                itemId: addOnMenuItem.itemId,
                name: addOnItem.name,
                priceCents: addOnMenuItem.priceCents,
            };
        } else {
            return null;
        }
    });
    const addOnsInfo: {
        itemId: string;
        name: string;
        priceCents: API.models.USDCents;
    }[] = unfilteredAddOnsInfo?.filter(
        (addOnInfo): addOnInfo is Exclude<typeof addOnInfo, null> =>
            addOnInfo !== null,
    );

    const [selectedAddOnIds, setSelectedAddOnIds] = useState<string[]>([]);

    const onSave = () => {
        if (selectedAddOnIds.length !== 0) {
            const itemCustomizationsToAdd = selectedAddOnIds.map((addOnId) => {
                return {
                    customizationId: uuid4(),
                    itemId: addOnId,
                    modifierSelections: {},
                    quantity: quantity,
                };
            });
            dispatch(
                posActions.addItems(
                    itemCustomizationsToAdd,
                    [],
                    false,
                    !showDigitalOnly,
                ),
            );
        }

        if (!grouping) {
            logger.warn(
                "Attempting to customize grouping. Grouping not found on save.",
                {
                    groupingId: groupingId,
                },
            );
            return;
        }

        dispatch(
            posActions.addItem(
                activeOptionId,
                quantity,
                modSelections,
                undefined,
                !showDigitalOnly,
            ),
        );

        if (
            !allergyWarningShown &&
            Object.keys(modSelections).find(
                (modId) => posAllPrepsMods.byId[modId]?.isAllergy,
            )
        ) {
            dispatch(posActions.setAllergyWarningShown(true));
            setShowAllergyWarning(true);
            return;
        }

        onClose();
    };

    const allPreps = useAppSelector((state) => state.pos.allPreps);

    const baseUnitPriceCents = getUnitPriceCents(
        menuItems[menuId ?? ""]?.[activeOptionId]?.priceCents,
        {},
        menuItemModifiers[menuId ?? ""][activeOptionId],
        allPreps,
    );
    const totalPriceCents =
        getUnitPriceCents(
            menuItems[menuId ?? ""]?.[activeOptionId]?.priceCents,
            modSelections,
            menuItemModifiers[menuId ?? ""][activeOptionId],
            allPreps,
        ) * quantity;

    const totalPriceFormatted =
        Lib.currency.centsToDollarString(totalPriceCents);

    if (!categoryId || !groupingId || !menuId || !grouping) {
        return null;
    }

    const updateModSelection = (
        modifierId: string,
        selectionId: string,
        quantity: number,
        isDefault: boolean,
        isRequired: boolean,
    ) => {
        setModSelections((prevState) => {
            if (
                prevState[modifierId]?.selectionId === selectionId &&
                prevState[modifierId]?.quantity === quantity
            ) {
                return prevState;
            }

            const nextModSelections = {...prevState};
            if (isDefault && !isRequired) {
                delete nextModSelections[modifierId];
            } else {
                nextModSelections[modifierId] = {selectionId, quantity};
            }

            // TODO: #56 - Do we need dirty tracking?
            return nextModSelections;
        });
    };

    const getExistingAllPrepsMods = (
        currentItemModSelections: API.models.ModifierSelectionDict,
    ) => {
        const appliedAllPrepsMods: API.models.ModifierSelectionDict = {};
        Object.keys(currentItemModSelections).forEach((modId) => {
            const allPrepsMod = allPreps.byId[modId];
            const currentItemModSelection = currentItemModSelections[modId];
            const allPrepsModSelection = allPrepsMod?.selections.find(
                (sel) => sel.id === currentItemModSelection.selectionId,
            );
            if (allPrepsMod && allPrepsModSelection) {
                appliedAllPrepsMods[modId] = currentItemModSelection;
            }
        });
        return appliedAllPrepsMods;
    };

    const onChangeOption = (optionId: string) => {
        setModSelections((prevModSelections) => {
            // this will get us the required mods for the option selected
            const initialModSelections = getInitialModSelections(
                itemsById,
                optionId,
                menuItems,
                menuId,
                menuItemModifiers,
                null,
                true,
                !showDigitalOnly,
            );
            const existingValidMods =
                Lib.selectors.getExistingValidModsForSubstitute(
                    menuItemModifiers[menuId ?? ""],
                    optionId,
                    prevModSelections,
                );
            const existingAllPrepsMods =
                getExistingAllPrepsMods(prevModSelections);

            return {
                ...initialModSelections,
                ...existingValidMods,
                ...existingAllPrepsMods,
            };
        });
        setActiveOptionId(optionId);
    };

    function renderAddOns() {
        if ((addOnsInfo?.length ?? 0) === 0) {
            return;
        }

        return (
            <div className={styles.addOnsContainer}>
                {strings.addOnsTitle}
                <div className={styles.addOnsOptionsContainer}>
                    {addOnsInfo.map((addOnInfo, i) => (
                        <ToggleButton
                            key={"add-on-" + i}
                            className={styles.addOnsOption}
                            checked={selectedAddOnIds.includes(
                                addOnInfo.itemId,
                            )}
                            label={addOnInfo.name}
                            label2={`+ ${Lib.currency.centsToDollarString(
                                addOnInfo.priceCents,
                            )}`}
                            onChange={(checked) => {
                                if (checked) {
                                    setSelectedAddOnIds([
                                        ...selectedAddOnIds,
                                        addOnInfo.itemId,
                                    ]);
                                } else {
                                    setSelectedAddOnIds([
                                        ...selectedAddOnIds.filter(
                                            (addOnId) =>
                                                addOnId !== addOnInfo.itemId,
                                        ),
                                    ]);
                                }
                            }}
                        />
                    ))}
                </div>
            </div>
        );
    }

    const activeItem = itemsById[activeOptionId];

    return (
        <>
            <Drawer
                size="large"
                onClose={() => navigate(routeHelpers.category(categoryId))}
                closeButtonLabel={strings.cancel}>
                <SplitDrawerContent
                    className={styles.content}
                    left={
                        <ItemEditor
                            itemId={activeOptionId}
                            itemKey={activeOptionId}
                            menuId={menuId}
                            modifierSelections={modSelections}
                            onChangeModifier={updateModSelection}
                            options={availableOptions}
                            onChangeOption={onChangeOption}
                            excludeDigitalOnly={!showDigitalOnly}
                        />
                    }
                    right={
                        <>
                            <div className={styles.titleAndPrice}>
                                {activeItem.isolatedImages352.length > 0 && (
                                    <div className={styles.imageContainer}>
                                        <RetinaImage
                                            retinaImages={
                                                activeItem.isolatedImages352
                                            }
                                            className={styles.image}
                                        />
                                    </div>
                                )}
                                <div
                                    className={cn(
                                        styles.title,
                                        activeItem.isolatedImages352.length > 0
                                            ? styles.titleMargin
                                            : null,
                                    )}>
                                    {activeItem.name}
                                </div>
                                <div className={styles.price}>
                                    {Lib.currency.centsToDollarString(
                                        baseUnitPriceCents,
                                    )}
                                </div>
                            </div>

                            <div className={styles.divider} />
                            <div className={styles.rightCustomizations}>
                                <CustomizationList
                                    modifierSelections={modSelections}
                                    itemId={activeItem.id}
                                    onChangeModifier={updateModSelection}
                                    editable={true}
                                />

                                <div className={styles.spacer} />
                            </div>
                            <div className={styles.divider} />
                            {renderAddOns()}
                            {matchedCombos && matchedCombos.length ? (
                                <OutlineButton
                                    className={styles.makeItAComboButton}
                                    label={strings.makeItACombo}
                                    labelClassName={styles.makeItAComboLabel}
                                    mode="blue"
                                    onClick={() => {
                                        if (matchedCombos.length > 1) {
                                            setComboSubstitutionsModalVisible(
                                                true,
                                            );
                                        } else {
                                            onClickCombo(
                                                matchedCombos[0].categoryIds[0],
                                                matchedCombos[0].id,
                                            );
                                        }
                                    }}
                                />
                            ) : null}
                            <div className={styles.divider} />

                            <div className={styles.qtyAddToOrder}>
                                <QuantityControl
                                    quantity={quantity}
                                    setQuantity={setQuantity}
                                />
                                <OutlineButton
                                    label={totalPriceFormatted}
                                    label2={strings.addToOrder}
                                    onClick={onSave}
                                    className={styles.fullWidth}
                                />
                            </div>
                        </>
                    }
                />
            </Drawer>
            {showAllergyWarning ? (
                <AllergyWarningModal onClose={onClose} />
            ) : null}
            {comboSubstitutionsModalVisible && matchedCombos && (
                <ComboSubstitutionsModal
                    combos={matchedCombos}
                    replacedEntreeItem={{
                        itemId: activeOptionId,
                        modifierSelections: modSelections,
                    }}
                    onClickCombo={onClickCombo}
                    onClose={() => setComboSubstitutionsModalVisible(false)}
                />
            )}
        </>
    );
}
